import { ArrowDown, ArrowUpRight, Clock3, MapPin, Phone, Quote } from 'lucide-react'
import { ArticleCard } from '../components/ArticleCard'
import { ProjectCard } from '../components/ProjectCard'
import { SectionHeading } from '../components/SectionHeading'
import { ServiceCard } from '../components/ServiceCard'
import {
  articles,
  notableClients,
  projects,
  services,
  site,
  testimonials,
} from '../content/site'

export function HomePage() {
  return (
    <>
      <section className="home-hero">
        <div className="shell home-hero__grid">
          <div className="home-hero__copy">
            <p className="eyebrow eyebrow--light">Southern California aquarium specialists</p>
            <h1>Aquariums that <em>belong</em> in the room.</h1>
            <p className="home-hero__lede">
              Custom design, expert installation, and ongoing care for aquatic environments that look intentional and stay healthy.
            </p>
            <div className="button-row">
              <a className="button button--accent" href="/contact/">
                Plan my aquarium <ArrowUpRight aria-hidden="true" />
              </a>
              <a className="text-link text-link--light" href={site.phoneHref}>
                <Phone aria-hidden="true" /> {site.phoneDisplay}
              </a>
            </div>
            <a className="scroll-cue" href="#services">
              <ArrowDown aria-hidden="true" /> Explore our work
            </a>
          </div>
          <div className="home-hero__visual">
            <img
              src="/images/optimized/hero.webp"
              alt="Vibrant reef aquarium designed and maintained by Aquatic Contractors"
              width="1500"
              height="754"
              fetchPriority="high"
            />
            <div className="home-hero__stamp">
              <strong>20+</strong>
              <span>years of hands-on experience</span>
            </div>
          </div>
        </div>
        <div className="shell proof-strip">
          <div><MapPin aria-hidden="true" /><span>Santa Barbara to San Diego</span></div>
          <div><Clock3 aria-hidden="true" /><span>Same-day emergency service when available</span></div>
          <div className="proof-strip__tanked">
            <img
              src="/images/optimized/as-seen-on-tanked-transparent.png"
              alt="As seen on Animal Planet's hit show Tanked"
              width="906"
              height="202"
            />
          </div>
        </div>
      </section>

      <section className="section services-intro" id="services">
        <div className="shell">
          <SectionHeading
            eyebrow="Built well. Kept well."
            title="One team from first sketch to thriving system."
            body="Beautiful aquariums are equal parts design, engineering, biology, and dependable care. We handle the whole picture."
          />
          <div className="service-grid">
            {services.map((service) => <ServiceCard key={service.id} service={service} />)}
          </div>
        </div>
      </section>

      <section className="section section--navy project-showcase">
        <div className="shell">
          <SectionHeading
            eyebrow="Selected work"
            title="Designed for the space around it."
            body="From living-room reefs to hospitality centerpieces, every system begins with the room, the animals, and the realities of long-term care."
            light
          />
          <div className="project-grid">
            {projects.map((project, index) => (
              <ProjectCard key={project.title} project={project} featured={index === 0} />
            ))}
          </div>
        </div>
      </section>

      <section className="section process-section">
        <div className="shell process-grid">
          <div className="process-story">
            <p className="eyebrow">Done right the first time</p>
            <h2>The expensive part is fixing what should have been planned.</h2>
            <p>
              Many customers call us after an aquarium was designed without enough thought for access, filtration, plumbing, or long-term care. We plan those systems together so the aquarium works just as well after the reveal.
            </p>
            <a className="button button--dark" href="/services/">
              See every service <ArrowUpRight aria-hidden="true" />
            </a>
          </div>
          <ol className="process-list">
            <li><span>01</span><div><h3>Assess</h3><p>We learn the room, system, animals, constraints, and what success looks like.</p></div></li>
            <li><span>02</span><div><h3>Design</h3><p>We coordinate the display, life-support systems, access, and service plan.</p></div></li>
            <li><span>03</span><div><h3>Build</h3><p>Installation is tested, tuned, stocked thoughtfully, and explained clearly.</p></div></li>
            <li><span>04</span><div><h3>Care</h3><p>Ongoing service protects the animals, equipment, and original design intent.</p></div></li>
          </ol>
        </div>
      </section>

      <section className="section client-section">
        <div className="shell">
          <div className="client-section__heading">
            <SectionHeading
              eyebrow="Known by the company we keep"
              title="Trusted in private homes and public spaces."
            />
            <p>Founder Brian Hoover has built and cared for systems for clients including:</p>
          </div>
          <div className="client-grid">
            {notableClients.map((client) => (
              <figure key={client.name} className="client-card">
                <img src={client.image} alt={client.alt} loading="lazy" />
                <figcaption>{client.name}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="section testimonial-section">
        <div className="shell testimonial-grid">
          <div className="testimonial-section__intro">
            <Quote aria-hidden="true" />
            <p className="eyebrow">Client notes</p>
            <h2>Rescued systems. Growing coral. Fewer surprises.</h2>
          </div>
          <div className="testimonial-list">
            {testimonials.map((testimonial, index) => (
              <blockquote key={testimonial.slice(0, 30)}>
                <span>0{index + 1}</span>
                <p>“{testimonial}”</p>
                <cite>Aquatic Contractors client</cite>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      <section className="section insight-preview">
        <div className="shell">
          <div className="split-heading">
            <SectionHeading eyebrow="Field notes" title="Advice from decades in the water." />
            <a className="text-link" href="/insights/">View all insights <ArrowUpRight aria-hidden="true" /></a>
          </div>
          <div className="article-grid">
            {articles.map((article) => <ArticleCard key={article.slug} article={article} />)}
          </div>
        </div>
      </section>
    </>
  )
}
